import React, { useMemo, useEffect, useCallback, useState, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { observer, inject } from 'mobx-react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { Box, Button, Card, Step, StepButton, Stepper, PrintIcon } from '@availity/element';

import ItemRenderer from './components/ItemRenderer';
import ReviewStep from './components/ReviewStep';

import ConfirmationModal from './components/ConfirmationModal/ConfirmationModal';
import ErrorModal from './components/ErrorModal';

import { buildBackendPayload } from '../utils/buildBackendPayload';

import CommonStore from '../Common/stores/CommonStore';
import ProviderStore from '../Common/stores/ProviderStore';
import IncidentStore from '../Common/stores/IncidentStore';

/**
 * ============================================================
 * FHIR + UI helper utilities
 * ============================================================
 * This file renders a step-wizard form from a FHIR R5 Questionnaire.
 * - We honor itemControl (drop-down, radio-button, page, help, etc.)
 * - We honor validation extensions like regex and minLength
 * - We support SDC initialExpression for default values (e.g., today())
 * - We gate navigation with validation and sync the active step to the URL
 */

/**
 * Optional UI extensions so authors can hint sizing and layout per field.
 * These are non-standard (org-specific) and safely ignored if missing.
 */
const UI_EXT = {
  size: 'http://elevancehealth.com/fhir/StructureDefinition/ui-size',
  grid: 'http://elevancehealth.com/fhir/StructureDefinition/ui-grid',
  cls: 'http://elevancehealth.com/fhir/StructureDefinition/ui-class',
  sx: 'http://elevancehealth.com/fhir/StructureDefinition/ui-sx',
};

const UI_EXT_NEW = {
  grid: 'gridStyle',
  fieldType: 'fieldType',
  sx: 'fieldStyle',
};

const isValidDate = (value) => {
  const date = new Date(value);
  return !Number.isNaN(date.getTime()); // Check if the date is valid
};

const dateComparisonInYears = (dateToCompare, op, age) => {
  const currentDate = new Date();
  const comparedDate = new Date(dateToCompare);

  // Calculate the difference in years
  let ageDifference = currentDate.getFullYear() - comparedDate.getFullYear();

  // Adjust for the month and day to ensure it's a full `age`
  const isFullYears =
    currentDate.getMonth() > comparedDate.getMonth() ||
    (currentDate.getMonth() === comparedDate.getMonth() && currentDate.getDate() >= comparedDate.getDate());

  // If the current date is before the birth date in the current year, subtract 1 from ageDifference
  if (!isFullYears) {
    ageDifference -= 1;
  }

  // Evaluate the operator dynamically
  switch (op) {
    case '<': {
      return ageDifference < age;
    }
    case '<=': {
      return ageDifference <= age;
    }
    case '>': {
      return ageDifference > age;
    }
    case '>=': {
      return ageDifference >= age;
    }
    case '!=': {
      return ageDifference !== age;
    }
    default: {
      return ageDifference === age; // Default to equality check
    }
  }
};

// Safely read a specific extension by URL off an item
const getExt = (item, url) => (item?.extension || []).find((e) => e.url === url);
const getExtNew = (item, system) => (item?.code || []).find((e) => e.system === system);

// Read Questionnaire.itemControl (radio-button, drop-down, page, help, hidden, etc.)
const getItemControl = (item) =>
  getExt(item, 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl')?.valueCodeableConcept?.coding?.[0]
    ?.code ||
  getExtNew(item, UI_EXT_NEW.fieldType)?.code ||
  null;

// A "page" is a group with itemControl = page
const isPageGroup = (it) => it?.type === 'group' && getItemControl(it) === 'page';

// Turn titles into URL-safe slugs; used to map steps to router paths
const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replaceAll(/[^\da-z]+/g, '-')
    .replaceAll(/(^-|-$)/g, '') || 'step';

// Support R5 SDC initialExpression (we currently implement today() only)
const getInitialExpression = (item) => {
  const expr = getExt(
    item,
    'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-initialExpression'
  )?.valueExpression;
  if (!expr) return null;
  const { language, expression } = expr;
  if (language === 'text/fhirpath' && String(expression).trim() === 'today()') {
    // Format yyyy-mm-dd so it binds correctly with <input type="date">
    return dayjs();
  }
  return null;
};

// Coerce values for numeric vs string comparisons (used by enableWhen)
const toNumberOrString = (v) => {
  if (v == null || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) && String(v).trim() !== '' ? n : String(v);
};

// Generic comparator used in enableWhen (supports =, !=, <, >, <=, >=)
const compare = (a, op, b) => {
  // Handle case where `a` is an array
  if (Array.isArray(a)) {
    switch (op) {
      case '!=': {
        return !a.includes(b);
      }
      default: {
        return a.includes(b);
      }
    }
  }

  const an = toNumberOrString(a);
  const bn = toNumberOrString(b);

  // Number comparison when both sides are numeric-like
  if (typeof an === 'number' && typeof bn === 'number') {
    switch (op) {
      case '<': {
        return an < bn;
      }
      case '>': {
        return an > bn;
      }
      case '<=': {
        return an <= bn;
      }
      case '>=': {
        return an >= bn;
      }
      case '!=': {
        return an !== bn;
      }
      default: {
        return an === bn;
      }
    }
  }

  // Fallback: string comparison
  const as = String(a);
  const bs = String(b);
  switch (op) {
    case '!=': {
      return as !== bs;
    }
    case '<': {
      return as < bs;
    }
    case '>': {
      return as > bs;
    }
    case '<=': {
      return as <= bs;
    }
    case '>=': {
      return as >= bs;
    }
    default: {
      return as === bs;
    }
  }
};

/**
 * Build default RHF form values:
 * - Read "initial" values (R4) if present
 * - Otherwise read SDC "initialExpression" (R5)
 * - Optionally merge in defaults from a QuestionnaireResponse (if provided)
 */
const buildDefaults = (questionnaire, response) => {
  const defaults = {};
  const groups = {}; // Initialize groups as a plain object

  const isBackButtonVisible = (questionnaire?.code || []).some(
    (codeObj) => codeObj.system === 'isBackButtonVisible' && codeObj.code?.toLowerCase() === 'true'
  );

  const isPrintButtonVisible = (questionnaire?.code || []).some(
    (codeObj) => codeObj.system === 'isPrintButtonVisible' && codeObj.code?.toLowerCase() === 'true'
  );

  const isStepperDisabled = (questionnaire?.code || []).some(
    (codeObj) => codeObj.system === 'isStepperDisabled' && codeObj.code?.toLowerCase() === 'true'
  );

  const setDefault = (linkId, value) => {
    if (linkId && value != null) defaults[linkId] = value;
  };

  // Walk Questionnaire items (recursive)
  const walkQ = (items) => {
    for (const it of items || []) {
      if (it.type === 'group') {
        if (it.repeats) {
          groups[it.linkId] = (it.item || []).map((child) => child.linkId); // Update groups as an object
        }
        walkQ(it.item);
      } else if (it.initial && it.initial.length > 0) {
        const init = it.initial[0];
        const v = init.valueString ?? init.valueDate ?? init.valueInteger ?? init.valueDecimal ?? null;
        if (v != null) setDefault(it.linkId, v);
      } else {
        // Support SDC initialExpression (e.g., today())
        const exprVal = getInitialExpression(it);
        if (exprVal != null) {
          setDefault(it.linkId, exprVal);
        } else if (it.type === 'date' && it.linkId) {
          // Date pickers must default to null (not undefined) to stay controlled
          defaults[it.linkId] = null;
        }
      }
    }
  };

  // Walk QuestionnaireResponse (if you want to pre-fill from a prior response)
  const walkR = (items) => {
    for (const ri of items || []) {
      if (ri.itemType === 'group' || (ri.item && ri.item.length > 0)) walkR(ri.item);
      if (ri.answer && ri.answer.length > 0) {
        const ans = ri.answer[0];
        let v = ans.valueString ?? ans.valueDate ?? ans.valueInteger ?? ans.valueDecimal ?? null;
        if (ans.valueCoding) v = ans.valueCoding.code ?? ans.valueCoding.display ?? v;
        if (v != null) setDefault(ri.linkId, v);
      }
    }
  };

  walkQ(questionnaire?.item);
  walkR(response?.item); // typically null for new forms
  return { defaults, groups, isBackButtonVisible, isPrintButtonVisible, isStepperDisabled }; // Return both defaults and groups
};

/**
 * EnableWhen resolver:
 * - Watches the whole form (RHF useWatch)
 * - Supports operators: =, !=, <, >, <=, >=, exists
 * - Supports answer types: string, integer, decimal, date, boolean, coding
 */
const useEnableWhen = (control) => {
  const watchAll = useWatch({ control });

  return (enableWhenArr, enableWhenArrFromCode, matchType = 'all') => {
    // Determine which array to use based on their validity
    const conditions = enableWhenArr?.length > 0 ? enableWhenArr : enableWhenArrFromCode;

    if (!conditions || conditions.length === 0) return true;
    // If neither array has valid conditions, return true
    const checkCondition = (condition) => {
      let q;
      let op;
      let expected;

      if (enableWhenArr?.length > 0) {
        q = condition.question;
        op = condition.operator || '=';
        expected =
          condition.answerString ??
          condition.answerInteger ??
          condition.answerDecimal ??
          condition.answerDate ??
          condition.answerCoding?.code ??
          condition.answerCoding?.display ??
          condition.answerBoolean ??
          null;
      } else if (enableWhenArrFromCode?.length > 0) {
        // Split the string by `:`
        const parts = condition?.code.split(':');

        const [question, operator, value, conditionType] = parts;
        q = question;
        op = operator;
        expected = value;

        if (conditionType === 'age()') {
          return watchAll?.[q] && isValidDate(watchAll?.[q])
            ? dateComparisonInYears(watchAll?.[q], op, Number(expected))
            : false;
        }
      }

      const currentVal = watchAll?.[q];
      if (op === 'exists') {
        return Boolean(expected) === !(currentVal == null || currentVal === '');
      }

      return compare(currentVal, op, expected);
    };

    return matchType === 'any'
      ? conditions.some((condition) => checkCondition(condition))
      : conditions.every((condition) => checkCondition(condition));
  };
};

// Helper to parse JSON safely
const parseJSON = (str, fallback = null) => {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};

// ---- Place your function here ----
const formatDynamicErrorMsg = (msg, repeatCount) => {
  if (
    typeof msg === 'string' &&
    typeof repeatCount === 'number' &&
    !Number.isNaN(repeatCount) &&
    /\${[^}]+}/.test(msg)
  ) {
    // Replace all ${...} placeholders with repeatCount + 1
    return msg.replaceAll(/\${[^}]+}/g, repeatCount + 1);
  }
  return msg;
};

/**
 * Return field properties for the item
 */

const extractItemProperties = (item, repeatCount) => {
  const result = {};

  // Helper to extract extensions
  const getExtensionValue = (url, key = 'valueString') => getExt(item, url)?.[key] || null;

  // Extract regex pattern
  const regexPattern = getExtensionValue('http://hl7.org/fhir/StructureDefinition/regex');

  // Extract arrays
  const extractArray = (system) => (item?.code || []).filter((codeObj) => codeObj.system === system && codeObj.code);

  // Extract numeric values
  const extractNumericValue = (system) =>
    parseJSON((item?.code || []).find((codeObj) => codeObj.system === system && codeObj.code)?.code) || '';

  // Extract placeholders and date texts
  const extractSingleValue = (system) =>
    (item?.code || []).find((codeObj) => codeObj.system === system && codeObj.code)?.code || '';

  // Extract minLength
  result.minLen = getExtensionValue('http://hl7.org/fhir/StructureDefinition/minLength', 'valueInteger');

  result.regex = regexPattern
    ? (() => {
        const match = regexPattern.match(/^\/(.*)\/[a-z]*$/i);
        try {
          return new RegExp(match ? match[1] : regexPattern);
        } catch {
          return null;
        }
      })()
    : null;

  // Extract UI props
  result.uiProps = {
    size: getExtensionValue(UI_EXT.size),
    className: getExtensionValue(UI_EXT.cls),
    sx: parseJSON(getExtNew(item, UI_EXT_NEW.sx)?.code),
    grid: (() => {
      const gridRaw = getExtNew(item, UI_EXT_NEW.grid)?.code;
      if (!gridRaw) return null;
      const unescaped = gridRaw.replaceAll('\\"', '"');
      return (
        parseJSON(unescaped, null) &&
        Object.fromEntries(Object.entries(parseJSON(unescaped)).map(([key, value]) => [key, Number(value)]))
      );
    })(),
  };

  result.isFieldToBeHidden = extractSingleValue('hidden') === 'true';
  result.showCharCount = extractSingleValue('showCharCount') === 'true';
  result.endDateRequired = !extractSingleValue('endDateRequired') || extractSingleValue('endDateRequired') === 'true';
  result.inline = !extractSingleValue('inline') || extractSingleValue('inline') === 'true';

  result.maxRepeatCount = extractNumericValue('maxRepeatCount');
  result.numberOfRows = extractNumericValue('numOfRows');

  result.enableWhenArrFromCode = extractArray('enableWhenTrue');
  result.fieldDescriptionArr = extractArray('fieldDescription');
  result.additionalTextDescriptionList = extractArray('additionalTextDescription');

  result.placeholder = extractSingleValue('placeHolder');
  result.startDateText = extractSingleValue('startDateText');
  result.endDateText = extractSingleValue('endDateText');
  result.customErrorMsg = formatDynamicErrorMsg(extractSingleValue('regexCustomValidationMsg'), repeatCount);
  result.requiredErrorMsg = formatDynamicErrorMsg(extractSingleValue('requiredErrorCustomMsg'), repeatCount);
  result.minLenCustomErrorMsg = formatDynamicErrorMsg(extractSingleValue('minLengthValidationCustomMsg'), repeatCount);
  result.noFutureDateErrorMsg = extractSingleValue('futureDateValidation');
  result.noPastDateErrorMsg = extractSingleValue('pastDateValidation');
  result.maxBackDays = extractNumericValue('maxBackDays');
  result.additionalTextType = extractSingleValue('additionalTextType');

  // Extract max selection properties for checkbox validation
  result.maxSelectionLimit = extractNumericValue('maxSelectionLimit');
  result.maxSelectionErrorMsg = formatDynamicErrorMsg(extractSingleValue('maxSelectionErrorMsg'), repeatCount);

  // Extract file selector properties
  result.fileSelectorProps = {};
  for (const codeObj of item?.code || []) {
    if (typeof codeObj.system === 'string' && codeObj.system.startsWith('fileSelectorProp_')) {
      const key = codeObj.system.split('fileSelectorProp_')[1];
      if (key === 'allowedFileTypes') {
        result.fileSelectorProps[key] = parseJSON(codeObj.code.replaceAll("'", '"'));
      } else if (['maxSize', 'maxAvScanRetries', 'maxFileSize', 'maxTotalSize', 'maxFiles'].includes(key)) {
        result.fileSelectorProps[key] = Number(codeObj.code);
      } else if (['multiple', 'enableDropArea', 'disabled'].includes(key)) {
        result.fileSelectorProps[key] = codeObj.code.toLowerCase() === 'true';
      } else {
        result.fileSelectorProps[key] = codeObj.code;
      }
    }
  }

  // Extract tooltip text
  if (item.linkId && Array.isArray(item?.item) && item?.item?.length === 1) {
    const helpItem = item?.item[0];
    result.tooltiptext = helpItem.linkId === `${item.linkId}_helpText` ? helpItem.text || '' : null;
  } else {
    result.tooltiptext = null;
  }

  const clearDependentConfig = (item?.code || []).find((c) => c.system === 'clearDependentFields');
  result.clearDependentFields = clearDependentConfig?.code 
    ? clearDependentConfig.code.split(',').map(f => f.trim()) 
    : null;

  const mutuallyExclusiveConfig = (item?.code || []).find((c) => c.system === 'mutuallyExclusiveOption');
  result.mutuallyExclusiveOption = mutuallyExclusiveConfig?.code || null;

  const populateFromConfig = (item?.code || []).find((c) => c.system === 'populateFrom');
  result.populateFrom = populateFromConfig?.code || null;

  const requiredAtLeastOneOfConfig = (item?.code || []).find((c) => c.system === 'requiredAtLeastOneOf');
  result.requiredAtLeastOneOf = requiredAtLeastOneOfConfig?.code
    ? requiredAtLeastOneOfConfig.code.split(',').map((f) => f.trim())
    : null;

  result.requiredAtLeastOneOfErrorMsg = extractSingleValue('requiredAtLeastOneOfErrorMsg');

  result.minDateFromField = extractSingleValue('minDateFromField');
  result.minDateFromFieldErrorMsg = extractSingleValue('minDateFromFieldErrorMsg');
  result.disabledWhen = extractSingleValue('disabledWhen');

  return result;
};

/**
 * function to reset the field and clear error
 */

const clearField = (fieldName, getValues, setValue, errors, clearErrors) => {
  if (getValues(fieldName)) {
    if (fieldName.endsWith('-start') || fieldName.endsWith('-end')) {
      setValue(fieldName, null);
    } else {
      const value = getValues(fieldName);
      if (value) {
        if (dayjs.isDayjs(value) && value.isValid()) {
          setValue(fieldName, null); // Set value to null if it's a valid dayjs object
        } else {
          setValue(fieldName, ''); // Clear value
        }
      }
    }
  }
  if (errors[fieldName]) {
    clearErrors(fieldName); // Clear errors for the field
  }
};

/**
 * function to focus a field when it has error
 */

const focusFieldByName = (name) => {
  if (!name) return;
  const field = document.querySelector(`[name="${name}"]`);

  if (field) {
    const focusable = field.querySelector('input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable && typeof focusable.focus === 'function') {
      focusable.focus();
    } else {
      field.focus();
    }
  }
};

/**
 * Pull the Questionnaire object from the MobX commonStore.
 * We support a few shapes to be resilient to upstream differences.
 */
const getQuestionnaireFromStore = (store) => {
  const t = store?.templates ?? store?.template ?? null;

  if (Array.isArray(t)) {
    const first = t[0];
    return first?.fhirPayload || first?.questionnaire || first || null;
  }

  if (t && typeof t === 'object') {
    return t.fhirPayload || t.questionnaire || t;
  }

  return store?.questionnaire || null;
};

// Extension flag that marks a step (group/page) as the Review step
const isReviewStepGroup = (group) =>
  !!getExt(group, 'http://elevancehealth.com/fhir/StructureDefinition/ui-review-step')?.valueBoolean;

/**
 * ============================================================
 * Main wizard component
 * ============================================================
 * - Builds steps from top-level groups (prefers itemControl=page)
 * - Syncs the active step with the route (/wizard/:step)
 * - Validates required fields before allowing forward navigation
 * - Renders a dynamic Review step (if present) using <ReviewStep/>
 */
const QuestionnaireWizard = ({ commonStore, providerStore, incidentStore }) => {
  const { openErrorModal, openConfirmationModal } = incidentStore;
  const { inPublicfacingFlow } = commonStore;

  const [groupCounts, setGroupCounts] = useState({});

  // 1) Read Questionnaire from the store
  const questionnaire = getQuestionnaireFromStore(commonStore);

  // 2) Build initial RHF values (from initial[] or initialExpression)
  const { defaults, groups, isBackButtonVisible, isPrintButtonVisible, isStepperDisabled } = useMemo(
    () => buildDefaults(questionnaire, null),
    [questionnaire]
  );

  // 3) Initialize react-hook-form
  const formMethods = useForm({ defaultValues: defaults, mode: 'onChange' });

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = formMethods;

  // If defaults change (e.g., new Questionnaire), reset the form
  useEffect(() => {
    reset(defaults);
  }, [defaults, reset]);

  // EnableWhen resolver instance (depends on live form values)
  const enableWhenOk = useEnableWhen(control);

  // Auto-populate fields that have populateFrom in their code array when their enableWhen becomes true.
  const allFormValues = useWatch({ control });
  useEffect(() => {
    const walk = (items) => {
      for (const item of items || []) {
        const { populateFrom } = extractItemProperties(item);
        if (populateFrom && enableWhenOk(item.enableWhen, [], item.enableBehavior)) {
          const sourceVal = allFormValues[populateFrom];
          const currentVal = allFormValues[item.linkId];
          if (sourceVal && sourceVal !== currentVal) {
            formMethods.setValue(item.linkId, sourceVal);
          }
        }
        if (item.item) walk(item.item);
      }
    };
    walk(questionnaire?.item);
  }, [allFormValues]);

  // When any field in a requiredAtLeastOneOf group gets a selection, clear errors on all sibling fields.
  useEffect(() => {
    if (!questionnaire) return;
    const walk = (items) => {
      for (const item of items || []) {
        const { requiredAtLeastOneOf } = extractItemProperties(item);
        if (requiredAtLeastOneOf && requiredAtLeastOneOf.length > 0) {
          const anySelected = requiredAtLeastOneOf.some((fieldId) => {
            const val = allFormValues[fieldId];
            return Array.isArray(val) ? val.length > 0 : !!val;
          });
          if (anySelected) {
            for (const fieldId of requiredAtLeastOneOf) {
              if (errors[fieldId]?.type === 'manual') clearErrors(fieldId);
            }
          }
        }
        if (item.item) walk(item.item);
      }
    };
    walk(questionnaire?.item);
  }, [allFormValues, questionnaire, errors, clearErrors]);

  // Clear dependent fields when a controlling field's value changes (clearDependentFields code)
  const { clearDepConfig, itemTypeByLinkId } = useMemo(() => {
    const config = [];
    const typeMap = {};
    const walkDep = (items) => {
      for (const item of items || []) {
        if (item.linkId) typeMap[item.linkId] = item.type;
        const { clearDependentFields } = extractItemProperties(item);
        if (clearDependentFields) config.push({ linkId: item.linkId, fields: clearDependentFields });
        if (item.item) walkDep(item.item);
      }
    };
    walkDep(questionnaire?.item);
    return { clearDepConfig: config, itemTypeByLinkId: typeMap };
  }, [questionnaire]);

  const controllingLinkIds = clearDepConfig.map((c) => c.linkId);
  const watchedControllingValues = useWatch({ control, name: controllingLinkIds });
  const prevControllingRef = useRef(null);
  useEffect(() => {
    const prevSnapshot = prevControllingRef.current;
    if (prevSnapshot !== null) {
      for (const [idx, { fields }] of clearDepConfig.entries()) {
        const prev = prevSnapshot[idx];
        const curr = watchedControllingValues[idx];
        if (prev !== undefined && prev !== curr) {
          for (const fieldId of fields) {
            if (itemTypeByLinkId[fieldId] === 'date') {
              formMethods.setValue(fieldId, null);
              if (errors[fieldId]) clearErrors(fieldId);
            } else {
              clearField(fieldId, getValues, formMethods.setValue, errors, clearErrors);
            }
          }
        }
      }
    }
    prevControllingRef.current = Array.isArray(watchedControllingValues)
      ? [...watchedControllingValues]
      : [watchedControllingValues];
  }, [watchedControllingValues, clearDepConfig, itemTypeByLinkId, formMethods, errors, clearErrors, getValues]);

  // 4) Router utilities and current /:step param
  const navigate = useNavigate();
  const { step: stepSlug } = useParams();

  // 5) Build step list from Questionnaire pages (or groups when no 'page')
  const steps = useMemo(() => {
    const top = questionnaire?.item || [];
    const pages = top.filter((it) => isPageGroup(it));
    const useItems = pages.length > 0 ? pages : top.filter((it) => it.type === 'group');

    // Ensure step paths are unique and stable
    const seen = new Set();
    const uniquePath = (base, idx) => {
      let p = base || `step-${idx + 1}`;
      let c = 1;
      while (seen.has(p)) {
        p = `${base}-${c}`;
        c += 1; // no-plusplus compliant
      }
      seen.add(p);
      return p;
    };

    return useItems.map((group, idx) => {
      const base = slugify(group.text || group.linkId);
      return {
        id: group.linkId,
        title: group.text || `Step ${idx + 1}`,
        path: uniquePath(base, idx),
        group,
      };
    });
  }, [questionnaire]);

  // Local state of current step index (mirrored with the URL)
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const { selectedOrganization } = providerStore;
    if (!selectedOrganization && !inPublicfacingFlow) {
      providerStore.resetStore();
      navigate('/', { replace: true });
    }
  }, [providerStore, inPublicfacingFlow, navigate]);

  // 6) Keep activeStep in sync with the URL (and redirect unknown slugs to first step)
  useEffect(() => {
    if (steps.length === 0) return;
    const idx = steps.findIndex((s) => s.path === stepSlug);
    if (idx === -1 || idx > activeStep) {
      navigate(`/wizard/${steps[0].path}`, { replace: true });
      setActiveStep(0);
      return;
    }
    setActiveStep(idx);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [stepSlug, steps, navigate]);

  // Function to handle adding a repeated group
  const handleAddGroup = async (groupId) => {
    const validateLastGroupAndUpdateCount = async () => {
      let isValid = true;

      const repeatCountVar = groupCounts[groupId] + 1 || 0; // Get the correct count based on groupId
      const validationPromises = []; // Array to hold promises
      for (let i = 0; i <= repeatCountVar; i++) {
        const groupValues = groups[groupId]; // Access the group values for the current index
        for (const key in groupValues) {
          if (groupValues[key] !== undefined) {
            // Check if the property is defined
            const value = i > 0 ? `${groupValues[key]}-${i}` : groupValues[key];
            validationPromises.push(trigger(value, { shouldFocus: true })); // Push the promise to the array
          }
        }
      }
      const results = await Promise.all(validationPromises); // Wait for all validations to complete
      isValid = results.every(Boolean); // Use Boolean directly

      if (isValid) {
        // Only update group counts if valid
        setGroupCounts((prevCounts) => ({
          ...prevCounts,
          [groupId]: (prevCounts[groupId] || 0) + 1,
        }));
      }
    };

    await validateLastGroupAndUpdateCount(); // Await the inner function
  };

  const clearValues = (childItem, repeatIndex, getValues, setValue, errors, clearErrors) => {
    if (!childItem) return; // Null check for childItem
    const controlType = getItemControl(childItem);

    if (controlType === 'dateRange') {
      clearField(`${childItem.linkId}-start`, getValues, setValue, errors, clearErrors);
      clearField(`${childItem.linkId}-end`, getValues, setValue, errors, clearErrors);
    } else {
      clearField(
        repeatIndex ? `${childItem.linkId}-${repeatIndex}` : childItem.linkId,
        getValues,
        setValue,
        errors,
        clearErrors
      );
    }

    // Recursively clear values and errors for child items
    if (childItem.item) {
      for (const nestedChild of childItem.item) {
        clearValues(nestedChild, repeatIndex, getValues, setValue, errors, clearErrors);
      }
    }
  };

  const clearItemValues = (item, groupCounts, getValues, setValue, errors, clearErrors) => {
    const repeatCount = (groupCounts[item.linkId] || 0) + 1;
    if (item.type === 'group') {
      for (let i = 0; i < (item.repeats ? repeatCount : 1); i++) {
        if (item.item) {
          for (const nestedChild of item.item) {
            clearValues(nestedChild, i, getValues, setValue, errors, clearErrors); // Clear values for group children
          }
        }
      }
    } else {
      const controlType = getItemControl(item);
      if (controlType === 'dateRange') {
        clearValues(item, null, getValues, setValue, errors, clearErrors);
      } else {
        clearValues(item, null, getValues, setValue, errors, clearErrors);
      }
    }
  };

  // 7) Gather required field names for a given step (respect enableWhen)
  const collectAllFieldNames = useCallback(
    (group) => {
      const names = [];
      const walk = (item, repeatIndex) => {
        const { enableWhenArrFromCode } = extractItemProperties(item);
        const controlType = getItemControl(item);
        if (!enableWhenOk(item.enableWhen, enableWhenArrFromCode, item.enableBehavior)) return; // skip disabled items
        if (item.type === 'group') {
          if (item.repeats && item.linkId) {
            const repeatCount = (groupCounts[item.linkId] || 0) + 1; // Get the correct count based on groupId
            for (let i = 0; i < repeatCount; i++) {
              for (const child of item.item || []) {
                walk(child, i); // Walk through child items for repeated groups
              }
            }
          } else {
            for (const child of item.item || []) {
              walk(child); // Walk through child items for non-repeated groups
            }
          }
          return;
        }

        // Handle non-group items
        const definedRepeatCount = repeatIndex || 0; // Define repeatCount if not already defined

        if (controlType === 'dateRange') {
          names.push(`${item.linkId}-start`, `${item.linkId}-end`); // Push both values for dateRange
        } else {
          names.push(definedRepeatCount > 0 ? `${item.linkId}-${definedRepeatCount}` : item.linkId);
        }
      };

      walk(group); // Start walking the group
      return names;
    },
    [enableWhenOk, groupCounts] // Added dependencies for groupCounts and groups
  );

  // Validate group-level constraints (e.g. requiredAtLeastOneOf) for a given step group.
  // Returns true if all constraints pass; otherwise sets errors on each listed field and returns false.
  const validateGroupConstraints = useCallback(
    (stepGroup) => {
      if (!stepGroup) return true;
      const { requiredAtLeastOneOf } = extractItemProperties(stepGroup);
      if (!requiredAtLeastOneOf || requiredAtLeastOneOf.length === 0) return true;

      const hasSelection = requiredAtLeastOneOf.some((fieldId) => {
        const val = getValues(fieldId);
        return Array.isArray(val) ? val.length > 0 : !!val;
      });

      if (!hasSelection) {
        // Flatten all items in the group to find each field's custom error message
        const allGroupItems = [];
        const flattenItems = (items) => {
          for (const it of items || []) {
            allGroupItems.push(it);
            if (it.item) flattenItems(it.item);
          }
        };
        flattenItems(stepGroup.item);

        // Set error on each field individually so each checkbox group turns red with its own message
        for (const fieldId of requiredAtLeastOneOf) {
          const fieldItem = allGroupItems.find((it) => it.linkId === fieldId);
          const { requiredErrorMsg } = fieldItem ? extractItemProperties(fieldItem) : {};
          const errorMsg = requiredErrorMsg || 'Please select at least one option';
          setError(fieldId, { type: 'manual', message: errorMsg });
        }
        return false;
      }

      // Clear any previously set manual errors on each field
      for (const fieldId of requiredAtLeastOneOf) {
        if (errors[fieldId]?.type === 'manual') clearErrors(fieldId);
      }
      return true;
    },
    [getValues, setError, clearErrors, errors]
  );

  // Validate the current step only (used when moving forward)
  const validateCurrentStep = useCallback(async () => {
    const stepGroup = steps[activeStep]?.group;
    if (!stepGroup) return true;

    const names = collectAllFieldNames(stepGroup);
    const isValid = await trigger(names);

    // Also run group-level constraints (e.g. requiredAtLeastOneOf)
    const groupConstraintsValid = validateGroupConstraints(stepGroup);

    if (!isValid) {
      const firstInvalidField = names.find((name) => !!errors[name]);
      focusFieldByName(firstInvalidField);
    }
    return isValid && groupConstraintsValid;
  }, [steps, activeStep, trigger, collectAllFieldNames, errors, validateGroupConstraints]);

  // Central navigation function:
  // - Prevents skipping required fields when moving forward
  // - Updates URL when navigation is allowed
  const goToStep = useCallback(
    async (index) => {
      if (index > activeStep) {
        const ok = await validateCurrentStep();
        if (!ok) return;
      }
      const bounded = Math.min(Math.max(index, 0), Math.max(steps.length - 1, 0));
      setActiveStep(bounded);

      if (steps[bounded]) {
        navigate(`/wizard/${steps[bounded].path}`, { replace: true });
      }
      clearErrors();
    },
    [activeStep, steps, clearErrors, navigate, validateCurrentStep]
  );

  // Button handlers (Next/Back) and Step click
  const handleNext = useCallback(() => {
    let nextStep = activeStep + 1;

    // Find the next step that is enabled
    for (let i = activeStep + 1; i < steps.length; i++) {
      const { group } = steps[i];
      const { enableWhenArrFromCode } = extractItemProperties(group);
      if (enableWhenOk(group.enableWhen, enableWhenArrFromCode, group.enableBehavior)) {
        nextStep = i; // Update nextStep if the current group is enabled
        break; // Exit loop once the next enabled step is found
      }
    }

    goToStep(nextStep);
  }, [goToStep, activeStep, steps, enableWhenOk]);

  const handleBack = useCallback(() => {
    let prevStep = activeStep - 1;

    if (prevStep === -1) {
      providerStore.resetStore();
      commonStore.resetStore();
      navigate('/', { replace: true });
      return;
    }

    // Find the previous step that is enabled
    for (let i = activeStep - 1; i >= 0; i--) {
      const { group } = steps[i];
      const { enableWhenArrFromCode } = extractItemProperties(group);
      if (enableWhenOk(group.enableWhen, enableWhenArrFromCode, group.enableBehavior)) {
        prevStep = i; // Update prevStep if the current group is enabled
        break; // Exit loop once the previous enabled step is found
      }
    }

    goToStep(prevStep);
  }, [activeStep, steps, navigate, goToStep, enableWhenOk, providerStore, commonStore]);

  const handleStepClick = useCallback(
    (index) => {
      // If clicking ahead, goToStep will validate the current step first
      goToStep(index);
    },
    [goToStep]
  );

  // Function to handle removing a repeated group
  const handleRemoveGroup = (groupId, item, repeatIndex, getValues, setValue, errors, clearErrors) => {
    if (item.type === 'group' && item.repeats && item.linkId) {
      // Clear values for the specified group
      for (const child of item.item || []) {
        clearValues(child, repeatIndex, getValues, setValue, errors, clearErrors); // Clear values for repeated group children
      }

      // Shift values from subsequent groups up
      const repeatCount = groupCounts[groupId] || 0;
      for (let i = repeatIndex + 1; i <= repeatCount; i++) {
        for (const child of item.item || []) {
          const currentValue = getValues(i ? `${child.linkId}-${i}` : child.linkId);
          setValue(i - 1 ? `${child.linkId}-${i - 1}` : child.linkId, currentValue || ''); // Move value up
        }
      }

      // Clear the last group's values after shifting
      for (const child of item.item || []) {
        clearValues(child, repeatCount, getValues, setValue, errors, clearErrors); // Clear values for the last group
      }
    }

    // Update the group counts
    setGroupCounts((prevCounts) => {
      const updatedCounts = { ...prevCounts };
      const currentCount = updatedCounts[groupId] || 0;
      if (currentCount > 0) {
        updatedCounts[groupId] = currentCount - 1; // Decrement the count directly
      }
      return updatedCounts;
    });
  };

  const focusFirstInvalidField = async () => {
    const stepGroup = steps[activeStep]?.group;
    if (!stepGroup) return true;

    const names = collectAllFieldNames(stepGroup);
    const firstInvalidField = names.find((name) => !!errors[name]);
    focusFieldByName(firstInvalidField);

    return true;
  };

  const onError = () => {
    focusFirstInvalidField();
  };

  // Validate group-level constraints across all enabled steps (called before final submit).
  // Returns true if all pass; false if any constraint fails (with errors set on the relevant fields).
  const validateAllGroupConstraints = useCallback(() => {
    const failingStep = steps.find((s) => {
      const { group } = s;
      const { enableWhenArrFromCode } = extractItemProperties(group);
      if (!enableWhenOk(group.enableWhen, enableWhenArrFromCode, group.enableBehavior)) return false;
      return !validateGroupConstraints(group);
    });
    return !failingStep;
  }, [steps, enableWhenOk, validateGroupConstraints]);

  // Final submit: values contains the entire flattened map { [linkId]: value }
  const onSubmit = useCallback(
    (values) => {
      // Validate cross-field group constraints before submitting
      if (!validateAllGroupConstraints()) return;

      const payload = buildBackendPayload(questionnaire, values, enableWhenOk, extractItemProperties, {
        providerStore,
        commonStore,
      });
      // await api.post("/your-endpoint", payload)
      incidentStore.submitIncident(payload);
    },
    [questionnaire, commonStore, enableWhenOk, incidentStore, providerStore, validateAllGroupConstraints]
  );

  // if formatting the file change the contentRef value, revert it. Else it will cause compilation errro
  const contentRef = useRef < HTMLDivElement > (null);

  // handle print functionality for RMIR
  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: 'Availity',
    pageStyle: '@page{ margin:0.1cm }',
  });

  // Guard: no data to render yet
  if (!questionnaire || steps.length === 0) return <div>No questionnaire</div>;

  // Current step's group (either a normal page or the review page)
  const currentGroup = steps[activeStep]?.group;

  // Create a new steps array and find the real active step
  const filteredSteps = steps.filter((s) => {
    const { group } = s;
    const { enableWhenArrFromCode } = extractItemProperties(group);
    return enableWhenOk(group.enableWhen, enableWhenArrFromCode, group.enableBehavior); // Include only enabled groups
  });

  // Find the position of the currentGroup in the filtered steps array
  const realActiveStepIndex = filteredSteps.findIndex((s) => s.id === steps[activeStep]?.group?.linkId);

  return (
    <Box id="questionnaire-wrapper">
      <ConfirmationModal isOpen={openConfirmationModal} />
      <ErrorModal isOpen={openErrorModal} />
      <Box id="questionnaire-box" ref={contentRef}>
        <Card id="questionnaire-card">
          {/* The form wraps the step contents so Enter key submits on the last step */}
          <form id="questionnaire-form" style={{ padding: 40 }}>
            {/* Stepper across the top; labels are the group/page titles */}
            {!isStepperDisabled && (
              <Stepper id="questionnaire-stepper" activeStep={realActiveStepIndex} alternativeLabel sx={{ mb: 2 }}>
                {steps.map((s, i) => {
                  const { group } = s;
                  const { enableWhenArrFromCode } = extractItemProperties(group);
                  if (
                    (group.enableWhen || enableWhenArrFromCode) &&
                    !enableWhenOk(group.enableWhen, enableWhenArrFromCode, group.enableBehavior)
                  )
                    return null; // skip disabled items
                  return (
                    <Step key={s.id} id={`step-${s.id}`}>
                      <StepButton id={`step-button-${s.id}`} onClick={() => handleStepClick(i)}>
                        {s.title}
                      </StepButton>
                    </Step>
                  );
                })}
              </Stepper>
            )}
            {isStepperDisabled && realActiveStepIndex !== 0 && (
              <Button id="back-button" onClick={handleBack} variant="outlined" color="secondary">
                Back
              </Button>
            )}

            {/* If the current group is marked as review via ui-review-step, render the review page */}
            {isReviewStepGroup(currentGroup) ? (
              <ReviewStep
                id="review-step"
                questionnaire={questionnaire}
                steps={filteredSteps}
                getValues={getValues}
                inPublicfacingFlow={inPublicfacingFlow}
              />
            ) : (
              // Otherwise, render the group recursively as inputs
              <ItemRenderer
                id="item-renderer"
                item={currentGroup}
                formMethods={formMethods}
                enableWhenOk={enableWhenOk}
                groupCounts={groupCounts}
                handleAddGroup={handleAddGroup}
                handleRemoveGroup={handleRemoveGroup}
                clearValues={clearValues}
                clearItemValues={clearItemValues}
                commonStore={commonStore}
                providerStore={providerStore}
                extractItemProperties={extractItemProperties}
                isPageGroup={isPageGroup}
                getItemControl={getItemControl}
              />
            )}

            {/* Navigation buttons; Submit only appears on the last step */}
            <Box
              id="navigation-buttons"
              sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}
            >
              {/* Left-aligned Print/Download button */}
              <Box>
                {isPrintButtonVisible && realActiveStepIndex === steps.length - 1 ? (
                  <Button
                    id="print-button"
                    color="secondary"
                    variant="outlined"
                    onClick={() => {
                      handlePrint();
                    }}
                  >
                    <PrintIcon id="print-icon" fontSize="xsmall" fontWeight="400" sx={{ mr: '0.2rem' }} />
                    Print/Download
                  </Button>
                ) : null}
              </Box>
              {/* Right-aligned navigation buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isBackButtonVisible || activeStep > 0 ? (
                  <Button id="back-button" onClick={handleBack} variant="outlined">
                    Back
                  </Button>
                ) : null}
                {activeStep < steps.length - 1 ? (
                  <Button id="next-button" onClick={handleNext} variant="contained">
                    Next
                  </Button>
                ) : (
                  <Button
                    id="submit-button"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent default form submission
                      handleSubmit(onSubmit, onError)(); // Explicitly call the submit handler
                    }}
                    variant="contained"
                    color="primary"
                  >
                    Submit
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        </Card>
      </Box>
    </Box>
  );
};

QuestionnaireWizard.propTypes = {
  commonStore: PropTypes.oneOf([CommonStore]),
  providerStore: PropTypes.oneOf([ProviderStore]),
  incidentStore: PropTypes.oneOf([IncidentStore]),
};

// Note: If you see a MobX warning about double observer wrapping,
// export with ONLY inject(...) OR ONLY observer(...). Many setups work with:
//   export default inject("commonStore")(QuestionnaireWizard);
export default inject('commonStore', 'providerStore', 'incidentStore')(observer(QuestionnaireWizard));
